import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  FileSpreadsheet,
  MessageSquare,
  CreditCard,
  FileText,
  ShieldCheck,
  CheckCircle2,
  TrendingUp,
  Clock,
  ArrowRight,
  Sparkles,
  Download,
  Printer,
  Scale,
  Users,
  Check,
  Zap,
  Lock,
  ChevronLeft,
  Share2,
  PhoneCall,
  HelpCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { CarteraPhBrochurePdfService } from '@/services/carteraPhBrochurePdfService';
import { toast } from 'sonner';

export default function CarteraPHBrochure() {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPdf = () => {
    try {
      setIsGeneratingPdf(true);
      toast.info('Generando Brochure comercial en PDF...');
      const doc = CarteraPhBrochurePdfService.generateBrochurePdf();
      doc.save('Brochure_Comercial_MiA_Cartera_PH.pdf');
      toast.success('¡Brochure descargado exitosamente!');
    } catch (error) {
      console.error('Error generando brochure PDF:', error);
      toast.error('No se pudo generar el PDF. Puede utilizar la opción de Imprimir / Guardar como PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800 flex flex-col selection:bg-emerald-100 selection:text-emerald-900 print:bg-white print:text-slate-900">
      <SEO
        title="Brochure Comercial | Recuperación de Cartera en Propiedad Horizontal - MiA Legal PH"
        description="Dossier comercial para administraciones y consejos de propiedad horizontal en Colombia. Notificaciones WhatsApp con PSE, Título Ejecutivo Ley 675 y blindaje Ley 2300."
      />

      {/* Reglas de impresión CSS para formato exacto Carta de 3 páginas */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              @page {
                size: letter portrait;
                margin: 10mm 12mm 10mm 12mm;
              }
              html, body {
                background: #ffffff !important;
                color: #0f172a !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                margin: 0 !important;
                padding: 0 !important;
                font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
              }
              .print-hidden, header, footer, nav {
                display: none !important;
              }
              .print-container {
                width: 100% !important;
                max-width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
                background: transparent !important;
                border: none !important;
                box-shadow: none !important;
              }
              .print-page {
                page-break-after: always !important;
                break-after: page !important;
                min-height: 250mm !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: space-between !important;
                padding: 0 !important;
                margin-bottom: 0 !important;
                border: none !important;
                box-shadow: none !important;
                background: #ffffff !important;
              }
              .print-page-last {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .print-no-break {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .print-header-bar {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                border-bottom: 2px solid #059669 !important;
                padding-bottom: 4px !important;
                margin-bottom: 12px !important;
              }
              .print-footer-bar {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                border-top: 1px solid #cbd5e1 !important;
                padding-top: 4px !important;
                margin-top: 10px !important;
                font-size: 8pt !important;
                color: #64748b !important;
              }
            }
          `
        }}
      />

      {/* Barra superior flotante de acciones (oculta al imprimir) */}
      <div className="print-hidden sticky top-0 z-50 bg-slate-900 text-white border-b border-slate-800 px-4 py-2.5 shadow-md">
        <div className="container mx-auto max-w-5xl flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/cartera-ph"
              className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Volver a Cartera PH</span>
            </Link>
            <span className="text-slate-600 hidden sm:inline">|</span>
            <span className="text-xs font-semibold text-emerald-400 hidden sm:inline flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" />
              Brochure Comercial de Ventas para Conjuntos y Administradores
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrint}
              className="bg-slate-800 border-slate-700 hover:bg-slate-700 text-white text-xs h-8 gap-1.5"
            >
              <Printer className="h-3.5 w-3.5 text-slate-300" />
              <span>Imprimir</span>
            </Button>

            <Button
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold h-8 gap-1.5 shadow-xs"
            >
              <Download className="h-3.5 w-3.5" />
              <span>{isGeneratingPdf ? 'Generando...' : 'Descargar PDF Formal'}</span>
            </Button>

            <Button
              size="sm"
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs h-8 gap-1.5 font-medium hidden md:inline-flex"
              asChild
            >
              <a
                href="https://wa.me/573507679981?text=Hola%2C%20acabo%20de%20revisar%20el%20Brochure%20Comercial%20de%20MiA%20Cartera%20PH%20y%20deseo%20agendar%20una%20demostraci%C3%B3n%20para%20el%20Consejo%20de%20mi%20conjunto."
                target="_blank"
                rel="noreferrer"
              >
                <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
                <span>Contactar Asesor</span>
              </a>
            </Button>
          </div>
        </div>
      </div>

      <div className="print-hidden">
        <Header />
      </div>

      <main className="flex-1 py-8 md:py-12 print:py-0">
        <div className="container mx-auto px-4 max-w-5xl print:max-w-none print:px-0 print-container">
          {/* ═════════════════════════════════════════════════════
              PÁGINA 1: PORTADA, MÉTRICAS & TABLA COMPARATIVA
              ═════════════════════════════════════════════════════ */}
          <section className="print-page bg-white rounded-3xl border border-slate-200/90 shadow-xl p-6 md:p-10 mb-8 print:mb-0 print:border-none print:shadow-none print:rounded-none">
            {/* Encabezado formal de página */}
            <div className="print-header-bar flex items-center justify-between pb-2 mb-4 border-b-2 border-emerald-600">
              <div>
                <span className="font-extrabold text-base md:text-lg text-slate-900 tracking-tight">MiA LEGAL PH</span>
                <span className="text-xs text-slate-500 block">Plataforma LegalTech de Recuperación de Cartera | Ley 675 de 2001 &bull; Ley 2300 de 2023</span>
              </div>
              <div className="text-right text-xs">
                <span className="font-bold text-emerald-800 block">WhatsApp: +57 350 767 9981</span>
                <span className="text-slate-500">contacto@miasistentelegalia.com</span>
              </div>
            </div>

            {/* Banner Portada Hero */}
            <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-slate-900 via-slate-950 to-emerald-950 text-white p-6 md:p-8 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge className="bg-emerald-600 text-white font-bold text-[11px] px-3 py-0.5 uppercase tracking-wider">
                  Dossier Comercial para Conjuntos y Administradores
                </Badge>
                <span className="text-xs text-slate-400 font-mono">Edición Ejecutiva 2026</span>
              </div>

              <div className="space-y-2">
                <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight">
                  Recupere la Cartera de su Copropiedad sin Intermediarios
                </h1>
                <p className="text-xs md:text-sm text-emerald-300 font-semibold">
                  Solución LegalTech con Botón de Pago PSE en WhatsApp, Título Ejecutivo Ley 675 y Blindaje Ley 2300
                </p>
                <p className="text-xs md:text-sm text-slate-300 leading-relaxed max-w-3xl">
                  MiA Cartera PH es la plataforma especializada para Administradores de Propiedad Horizontal y Consejos en Colombia. Integramos recaudo por WhatsApp con botón PSE directo a la cuenta bancaria del conjunto, liquidación de intereses moratorios a la tasa legal vigente de la Superfinanciera (Ley 675) y blindaje algorítmico contra sanciones de la Ley 2300 de 2023 ("Dejen de Fregar").
                </p>
              </div>

              {/* 4 Métricas de Impacto */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 pt-3 border-t border-white/10 text-center">
                <div className="p-2.5 rounded-xl bg-white/10 border border-white/10">
                  <div className="text-xl md:text-2xl font-extrabold text-emerald-400 font-mono">+45%</div>
                  <div className="text-[11px] font-semibold text-white">Recaudo en 15 Días</div>
                  <div className="text-[10px] text-slate-300">En etapa preventiva</div>
                </div>
                <div className="p-2.5 rounded-xl bg-white/10 border border-white/10">
                  <div className="text-xl md:text-2xl font-extrabold text-sky-400 font-mono">30 Seg</div>
                  <div className="text-[11px] font-semibold text-white">Pago Inmediato PSE</div>
                  <div className="text-[10px] text-slate-300">Sin salir de WhatsApp</div>
                </div>
                <div className="p-2.5 rounded-xl bg-white/10 border border-white/10">
                  <div className="text-xl md:text-2xl font-extrabold text-amber-400 font-mono">1 Clic</div>
                  <div className="text-[11px] font-semibold text-white">Título Ejecutivo PDF</div>
                  <div className="text-[10px] text-slate-300">Art. 48 Ley 675 para juzgado</div>
                </div>
                <div className="p-2.5 rounded-xl bg-white/10 border border-white/10">
                  <div className="text-xl md:text-2xl font-extrabold text-teal-400 font-mono">100%</div>
                  <div className="text-[11px] font-semibold text-white">Blindaje Ley 2300</div>
                  <div className="text-[10px] text-slate-300">Cero sanciones de la SIC</div>
                </div>
              </div>
            </div>

            {/* Diagnóstico del Problema */}
            <div className="space-y-2 pt-4">
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-bold">1</span>
                El Desafío Financiero en los Conjuntos Residenciales
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                En Colombia, entre el 12% y el 28% del presupuesto anual de una copropiedad se encuentra congelado en cuotas morosas. Los métodos habituales (cartas impresas bajo puerta, llamadas telefónicas o mensajes personales) desgastan al administrador, no brindan inmediatez para pagar y exponen a la administración a sanciones millonarias de la SIC (Ley 2300 de 2023).
              </p>
            </div>

            {/* Cuadro Comparativo */}
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-xs">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-900 text-white text-[11px]">
                    <th className="p-2.5 font-bold w-1/4">VARIABLE</th>
                    <th className="p-2.5 font-bold w-3/8 text-slate-300">COBRO TRADICIONAL / ABOGADOS</th>
                    <th className="p-2.5 font-bold w-3/8 text-emerald-400 bg-slate-950">CON MiA CARTERA PH</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="bg-white">
                    <td className="p-2 font-bold text-slate-900">Costo para el PH</td>
                    <td className="p-2 text-rose-700 font-medium">15% a 30% de honorarios sobre el recaudo.</td>
                    <td className="p-2 text-emerald-900 font-semibold bg-emerald-50/50">Tarifa fija mensual sin comisiones sobre el dinero.</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="p-2 font-bold text-slate-900">Forma de Pago</td>
                    <td className="p-2 text-rose-700 font-medium">Consignación en banco y recibos manuales.</td>
                    <td className="p-2 text-emerald-900 font-semibold bg-emerald-50/50">Botón oficial de pago PSE directo en WhatsApp.</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="p-2 font-bold text-slate-900">Ley 2300 (SIC)</td>
                    <td className="p-2 text-rose-700 font-medium">Alto riesgo de llamadas y mensajes a deshoras.</td>
                    <td className="p-2 text-emerald-900 font-semibold bg-emerald-50/50">Blindaje algorítmico 100% y bitácora legal auditable.</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="p-2 font-bold text-slate-900">Cobro Judicial</td>
                    <td className="p-2 text-rose-700 font-medium">Semanas calculando intereses y certificaciones.</td>
                    <td className="p-2 text-emerald-900 font-semibold bg-emerald-50/50">1 Clic para descargar Título Ejecutivo formal (Art. 48).</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="p-2 font-bold text-slate-900">Convivencia</td>
                    <td className="p-2 text-rose-700 font-medium">Tensiones, discusiones y reclamos en asambleas.</td>
                    <td className="p-2 text-emerald-900 font-semibold bg-emerald-50/50">Comunicación institucional con portal de autogestión.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Pie de página 1 */}
            <div className="print-footer-bar flex items-center justify-between pt-2 border-t text-[11px] text-slate-500">
              <span>MiA Cartera PH &bull; Dossier Comercial Confidencial</span>
              <span>Página 1 de 3</span>
            </div>
          </section>

          {/* ═════════════════════════════════════════════════════
              PÁGINA 2: LOS 6 PILARES & FLUJO OPERATIVO EN 3 PASOS
              ═════════════════════════════════════════════════════ */}
          <section className="print-page bg-white rounded-3xl border border-slate-200/90 shadow-xl p-6 md:p-10 mb-8 print:mb-0 print:border-none print:shadow-none print:rounded-none">
            {/* Encabezado formal de página */}
            <div className="print-header-bar flex items-center justify-between pb-2 mb-4 border-b-2 border-emerald-600">
              <div>
                <span className="font-extrabold text-base md:text-lg text-slate-900 tracking-tight">MiA LEGAL PH</span>
                <span className="text-xs text-slate-500 block">Arquitectura de Cobranza &bull; Ley 675 de 2001 &bull; Ley 2300 de 2023</span>
              </div>
              <div className="text-right text-xs">
                <span className="font-bold text-emerald-800 block">WhatsApp: +57 350 767 9981</span>
                <span className="text-slate-500">www.miasistentelegalia.com</span>
              </div>
            </div>

            <div className="space-y-1.5 mb-3">
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-bold">2</span>
                Los 6 Pilares Tecnológicos de la Plataforma
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                Acompañamiento integral en cada fase procesal para recuperar la cartera de forma rápida, respetuosa y jurídicamente blindada:
              </p>
            </div>

            {/* Cuadrícula de los 6 Pilares */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">1</span>
                    <strong className="text-xs text-slate-900 font-bold">Carga Masiva desde Excel</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-emerald-800 border-emerald-300 bg-white">Universal</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Exporte el balance de Daytona, SISCO, Siigo, WordOffice o su hoja de cálculo y súbalo en 3 segundos. Mapeo automático de unidades, cuotas y propietarios.
                </p>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">2</span>
                    <strong className="text-xs text-slate-900 font-bold">WhatsApp con Botón PSE</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-sky-800 border-sky-300 bg-white">Meta Oficial</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Requerimiento formal con enlace de pago seguro directo a la cuenta bancaria de la copropiedad. El residente paga desde su banco en 30 segundos.
                </p>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">3</span>
                    <strong className="text-xs text-slate-900 font-bold">Semáforo de Mora Ley 675</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-amber-800 border-amber-300 bg-white">Ley 675</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Cadencia legal: Preventiva (1-30d), Prejurídico Art. 48 (31-60d) y Prejudicial de demanda y embargo (&gt;60d) con liquidación oficial de intereses SFC.
                </p>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">4</span>
                    <strong className="text-xs text-slate-900 font-bold">Título Ejecutivo Art. 48 en 1 Clic</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-indigo-800 border-indigo-300 bg-white">Juzgados</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Certificado oficial firmado por el administrador con intereses moratorios discriminados a la tasa de usura vigente, listo para radicar demanda de embargo.
                </p>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">5</span>
                    <strong className="text-xs text-slate-900 font-bold">Portal de Acuerdos &amp; Paz y Salvos</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-teal-800 border-teal-300 bg-white">Ley 527</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  El copropietario simula cuotas autorizadas, suscribe un Contrato de Transacción Extrajudicial formal y descarga automáticamente su Paz y Salvo.
                </p>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">6</span>
                    <strong className="text-xs text-slate-900 font-bold">Blindaje Ley 2300 de 2023</strong>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-rose-800 border-rose-300 bg-white">SIC 100%</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Bloqueo estricto fuera de horarios legales (L-V 7am-7pm, Sáb 8am-3pm, Domingos y festivos bloqueados). Bitácora inmutable auditable para asambleas.
                </p>
              </div>
            </div>

            {/* Flujo en 3 Pasos */}
            <div className="p-4 rounded-2xl border border-emerald-600/60 bg-white space-y-2.5 shadow-xs">
              <div className="text-xs font-bold text-emerald-900 uppercase tracking-wide">
                Flujo Operativo Sencillo para el Administrador (3 Pasos)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <div className="text-emerald-700 font-mono font-bold text-xs">PASO 01</div>
                  <div className="font-bold text-xs text-slate-900">Exportar &amp; Subir Excel</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    Exporte el balance de cartera de su software contable actual y súbalo a MiA en 1 clic. El sistema clasifica los saldos al instante.
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <div className="text-emerald-700 font-mono font-bold text-xs">PASO 02</div>
                  <div className="font-bold text-xs text-slate-900">Despacho Automatizado PSE</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    El motor verifica horarios legales y despacha notificaciones personalizadas con enlace seguro de pago a WhatsApp y Correo.
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <div className="text-emerald-700 font-mono font-bold text-xs">PASO 03</div>
                  <div className="font-bold text-xs text-slate-900">Recaudo Directo o Embargo</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    El residente paga desde su banco en 30 segundos, o se genera el Título Ejecutivo Art. 48 para radicar demanda de embargo.
                  </p>
                </div>
              </div>
            </div>

            {/* Pie de página 2 */}
            <div className="print-footer-bar flex items-center justify-between pt-2 border-t text-[11px] text-slate-500">
              <span>MiA Cartera PH &bull; Dossier Comercial Confidencial</span>
              <span>Página 2 de 3</span>
            </div>
          </section>

          {/* ═════════════════════════════════════════════════════
              PÁGINA 3: PLANES, RETORNO (ROI), PREGUNTAS & CONTACTO
              ═════════════════════════════════════════════════════ */}
          <section className="print-page print-page-last bg-white rounded-3xl border border-slate-200/90 shadow-xl p-6 md:p-10 mb-8 print:mb-0 print:border-none print:shadow-none print:rounded-none">
            {/* Encabezado formal de página */}
            <div className="print-header-bar flex items-center justify-between pb-2 mb-4 border-b-2 border-emerald-600">
              <div>
                <span className="font-extrabold text-base md:text-lg text-slate-900 tracking-tight">MiA LEGAL PH</span>
                <span className="text-xs text-slate-500 block">Propuesta Económica &bull; Planes &bull; Retorno de Inversión (ROI)</span>
              </div>
              <div className="text-right text-xs">
                <span className="font-bold text-emerald-800 block">WhatsApp: +57 350 767 9981</span>
                <span className="text-slate-500">contacto@miasistentelegalia.com</span>
              </div>
            </div>

            <div className="space-y-1.5 mb-3">
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-bold">3</span>
                Planes de Suscripción Mensual por Copropiedad
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                Tarifas fijas transparentes en pesos colombianos (COP). Sin cobro de comisiones ni porcentajes sobre el capital que recupere la copropiedad:
              </p>
            </div>

            {/* Tarjetas de los 3 Planes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/70 flex flex-col justify-between space-y-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Copropiedad Pequeña</div>
                  <div className="text-[10px] text-slate-400">Hasta 80 unidades privadas</div>
                  <div className="text-2xl font-extrabold text-slate-900 font-mono mt-1">$350.000</div>
                  <div className="text-[10px] text-emerald-800 font-bold">COP / mes por copropiedad</div>
                  <div className="mt-2 p-1.5 rounded-lg bg-white border border-slate-200 text-[10px] space-y-0.5">
                    <div className="font-semibold text-emerald-800">250 WhatsApp PSE / mes</div>
                    <div className="text-slate-600">500 Correos / mes</div>
                  </div>
                </div>
                <ul className="text-[10px] text-slate-600 space-y-1 pt-1 border-t border-slate-200">
                  <li>✓ Carga masiva desde Excel</li>
                  <li>✓ Semáforo de mora Ley 675</li>
                  <li>✓ Título Ejecutivo Art. 48 en PDF</li>
                  <li>✓ Blindaje y bitácora Ley 2300</li>
                </ul>
              </div>

              <div className="p-4 rounded-2xl border-2 border-emerald-500 bg-white flex flex-col justify-between space-y-2 shadow-md shadow-emerald-500/10 relative">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white font-bold text-[9px] px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Más Recomendado
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Copropiedad Mediana</div>
                  <div className="text-[10px] text-slate-500">81 a 200 unidades privadas</div>
                  <div className="text-2xl font-extrabold text-slate-900 font-mono mt-1">$650.000</div>
                  <div className="text-[10px] text-emerald-800 font-bold">COP / mes por copropiedad</div>
                  <div className="mt-2 p-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[10px] space-y-0.5">
                    <div className="font-semibold text-emerald-900">600 WhatsApp PSE / mes</div>
                    <div className="text-slate-700">1.200 Correos / mes</div>
                  </div>
                </div>
                <ul className="text-[10px] text-slate-700 space-y-1 pt-1 border-t border-emerald-200">
                  <li className="font-semibold text-emerald-800">✓ Todo lo del Plan Pequeño</li>
                  <li>✓ Portal de Acuerdos de Pago</li>
                  <li>✓ Generación de Paz y Salvos</li>
                  <li>✓ Soporte prioritario para el Admin</li>
                  <li>✓ Informe ejecutivo para el Consejo</li>
                </ul>
              </div>

              <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/70 flex flex-col justify-between space-y-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Copropiedad Grande</div>
                  <div className="text-[10px] text-slate-400">Más de 200 unidades privadas</div>
                  <div className="text-2xl font-extrabold text-slate-900 font-mono mt-1">$1.150.000</div>
                  <div className="text-[10px] text-emerald-800 font-bold">COP / mes por copropiedad</div>
                  <div className="mt-2 p-1.5 rounded-lg bg-white border border-slate-200 text-[10px] space-y-0.5">
                    <div className="font-semibold text-emerald-800">1.500 WhatsApp PSE / mes</div>
                    <div className="text-slate-600">3.000 Correos / mes</div>
                  </div>
                </div>
                <ul className="text-[10px] text-slate-600 space-y-1 pt-1 border-t border-slate-200">
                  <li className="font-semibold text-slate-900">✓ Todo lo del Plan Mediano</li>
                  <li>✓ Personalización con logo del PH</li>
                  <li>✓ Títulos ejecutivos masivos</li>
                  <li>✓ Capacitación en vivo para Consejo</li>
                  <li>✓ Asesoría procesal en Ley 675</li>
                </ul>
              </div>
            </div>

            {/* Banner Multicopropiedad */}
            <div className="p-3 rounded-xl border border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
              <div>
                <span className="font-bold text-slate-900">¿Administra varias copropiedades a la vez o es Empresa de Administración?</span>
                <p className="text-[11px] text-slate-600">
                  Tarifas preferenciales por volumen con panel multi-PH: <strong>15% dto (2-3 PHs)</strong>, <strong>25% dto (4-6 PHs)</strong> y <strong>35% dto (7+ PHs)</strong>.
                </p>
              </div>
              <Badge variant="outline" className="border-emerald-500 text-emerald-800 bg-white font-mono text-[10px] shrink-0">
                Desde $227.500 / PH
              </Badge>
            </div>

            {/* Retorno de Inversión (ROI) y Contacto Comercial */}
            <div className="rounded-2xl bg-slate-900 text-white p-5 space-y-3">
              <div className="text-xs font-bold text-emerald-400 uppercase tracking-wide">
                Cómo se Paga Solo: Retorno de Inversión Comprobado (ROI)
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                En un conjunto de 120 apartamentos con cuota de $350.000 COP, <strong>recuperar solo 2 cuotas morosas en el mes ($700.000 COP) cubre con creces el costo del Plan Mediano ($650.000 COP)</strong>. Al ingresar entre 8 y 15 cuotas adicionales gracias al botón PSE en WhatsApp, la copropiedad obtiene un retorno de inversión real superior a 9x su inversión mensual.
              </p>
              <div className="pt-2 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="space-y-0.5 text-center sm:text-left">
                  <div className="text-xs font-bold text-emerald-400">Solicite una Demostración de 15 Minutos con su Archivo Real</div>
                  <div className="text-[11px] text-slate-400">WhatsApp: +57 350 767 9981 &bull; Email: contacto@miasistentelegalia.com</div>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs gap-1.5 shrink-0 print-hidden"
                  asChild
                >
                  <a
                    href="https://wa.me/573507679981?text=Hola%2C%20deseo%20agendar%20una%20demostraci%C3%B3n%20de%20MiA%20Cartera%20PH%20para%20mi%20copropiedad."
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Agendar por WhatsApp
                  </a>
                </Button>
              </div>
            </div>

            {/* Pie de página 3 */}
            <div className="print-footer-bar flex items-center justify-between pt-2 border-t text-[11px] text-slate-500">
              <span>MiA Cartera PH &bull; Dossier Comercial Confidencial</span>
              <span>Página 3 de 3</span>
            </div>
          </section>
        </div>
      </main>

      <div className="print-hidden">
        <Footer />
      </div>
    </div>
  );
}
